import React, {useEffect, useState} from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';
import {useLocation} from 'react-router-dom';

import {STATUS_REDUCER_STATE_ID} from '../status_constants';
import {loadNewVersion, setNewVersion} from '../status_thunks';
import {ConfigObjectType} from 'src/js/common';
import ActivityBasedTimer from 'src/js/common/utils/activity_based_timer';
import {ConfirmDialog} from 'src/js/components/ConfirmDialog';
import {useApiQuery} from 'src/js/hooks/useApiQuery';
import {useAppDispatch, useAppSelector} from 'src/js/store_hooks';

const I18N = defineMessages({
  DIALOG_TEXT: {
    id: 'common.checkNewVersion.confirmDialog.text',
    defaultMessage:
      'New version of application is available, please confirm to load new version.',
    description: 'content text of confirm dialog',
  },
  DIALOG_TITLE: {
    id: 'common.checkNewVersion.confirmDialog.title',
    defaultMessage: 'New version of application',
    description: 'title text of confirm dialog',
  },
});

interface OwnProps {
  config: ConfigObjectType;
}

type Props = OwnProps;

export const CheckNewAppVersionContainer: React.FC<Props> = (props) => {
  const {config} = props;
  const [versioningTimer] = useState(ActivityBasedTimer());
  const [isDialogOpened, setIsDialogOpened] = useState(false);
  const location = useLocation();
  const dispatch = useAppDispatch();
  const newVersionAvailable = useAppSelector(
    (state) => state[STATUS_REDUCER_STATE_ID].newVersionAvailable
  );

  const {data, refetch} = useApiQuery<string>(
    [config.service.url.appIndexFile],
    null,
    {
      axiosConfig: {url: config.service.url.appIndexFile},
      reactQueryOptions: {
        enabled: false,
      },
    }
  );

  useEffect(() => {
    // init checker - save current index.html on component mount
    // dispatch(checkNewVersion(config.service.url.appIndexFile));
    refetch();

    const timerId = versioningTimer.setInterval({
      callback: () => refetch(),
      // dispatch(checkNewVersion(config.service.url.appIndexFile)),
      interval: versioningTimer.STANDARD_INTERVAL,
      forcedInterval: versioningTimer.LONG_TIME_INTERVAL,
    });

    return () => {
      if (versioningTimer) {
        // clear interval on unmount
        versioningTimer.clearInterval(timerId);
      }
    };
  }, [config, dispatch, versioningTimer, refetch]);

  // check timer everytime when location has changed
  useEffect(() => {
    if (versioningTimer) {
      // check new version on route change
      versioningTimer.runTimersCheck();
    }
  }, [versioningTimer, location]);

  useEffect(() => {
    setIsDialogOpened(newVersionAvailable);
  }, [newVersionAvailable]);

  useEffect(() => {
    if (data) {
      dispatch(setNewVersion(data));
    }
  }, [data, dispatch]);

  const handleDialogCancel = () => {
    // do nothing
  };

  const handleDialogConfirm = () => {
    setIsDialogOpened(false);
    dispatch(loadNewVersion());
  };

  return (
    <ConfirmDialog
      isOpened={isDialogOpened}
      handleConfirm={handleDialogConfirm}
      handleCancel={handleDialogCancel}
      showCancel={false}
      titleText={I18N.DIALOG_TITLE.id}
    >
      <FormattedMessage {...I18N.DIALOG_TEXT} />
    </ConfirmDialog>
  );
};

CheckNewAppVersionContainer.displayName = 'CheckNewAppVersionContainer';
